import {WriteFileInput, WriteButton, WriteIconButton} from '../common/buttons/WriteActions';
import {Alert, Box, Card, CardMedia, Stack, Typography} from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {ChangeEvent, useEffect, useId, useState} from 'react';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';

const MAX_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

type Props = {file: File | null; onChange: (file: File | null) => void; disabled?: boolean};

export default function ScreenshotPicker({file, onChange, disabled}: Props) {
    const {t} = useAppTranslation();
    const inputId = useId();
    const [error, setError] = useState('');
    const [preview, setPreview] = useState('');

    useEffect(() => {
        if (!file) { setPreview(''); return; }
        const url = URL.createObjectURL(file);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    const select = (event: ChangeEvent<HTMLInputElement>) => {
        const next = event.target.files?.[0] ?? null;
        event.target.value = '';
        if (!next) return;
        if (!ALLOWED_TYPES.has(next.type)) { setError(String(t('feedback:validation.file_type' as never))); return; }
        if (next.size > MAX_SIZE) { setError(String(t('feedback:validation.file_size' as never))); return; }
        setError('');
        onChange(next);
    };

    return <Stack spacing={1}>
        <WriteFileInput hidden id={inputId} type="file" accept="image/jpeg,image/png,image/webp" onChange={select} disabled={disabled}/>
        <Box><WriteButton component="label" htmlFor={inputId} variant="outlined" disabled={disabled} startIcon={<AddPhotoAlternateOutlinedIcon/>}>{t('feedback:add_screenshot' as never)}</WriteButton></Box>
        <Typography variant="caption" color="text.secondary">{t('feedback:screenshot_hint' as never)}</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {file && <Card variant="outlined" sx={{position: 'relative', width: 'min(100%, 480px)'}}>
            <WriteIconButton aria-label={t('common:delete')} disabled={disabled} onClick={() => {setError(''); onChange(null);}} sx={{position: 'absolute', right: 8, top: 8, bgcolor: 'background.paper', zIndex: 1}}><CloseIcon/></WriteIconButton>
            <CardMedia component="img" src={preview} alt={t('feedback:screenshot_preview' as never)} sx={{maxHeight: 320, objectFit: 'contain'}}/>
            <Typography variant="caption" sx={{display: 'block', p: 1, overflowWrap: 'anywhere'}}>{file.name}</Typography>
        </Card>}
    </Stack>;
}
